import { createHash, randomBytes } from "node:crypto";
import { execFile } from "node:child_process";
import { Whop } from "@whop/sdk";
import { oauthBaseUrl, apiBaseUrl } from "./client";
import { startCallbackServer } from "./callback-server";
import { deriveProfileName, listProfiles, upsertProfile } from "./profiles";
import {
	type CompanyChoice,
	printInfo,
	selectCompany,
	startSpinner,
} from "./prompts";
import type { Identity } from "./types";

/** The registered public OAuth client for the Whop CLI. */
export const CLI_CLIENT_ID = "app_IoPWUwtO8c2gGA";

/** Fixed local callback port — must match the redirect_uri registered on the OAuth app. */
export const CLI_REDIRECT_URI = "http://localhost:13337/callback";

import { WHOP_SCOPES, type WhopScope } from "./scopes";

export { WHOP_SCOPES, type WhopScope };

/** Scopes requested by the CLI during OAuth login. */
export const CLI_SCOPES: WhopScope[] = [...WHOP_SCOPES];

// --- PKCE ---

export function generatePKCE(): { verifier: string; challenge: string } {
	const verifier = randomBytes(32).toString("base64url");
	const challenge = createHash("sha256").update(verifier).digest("base64url");
	return { verifier, challenge };
}

export function generateState(): string {
	return randomBytes(16).toString("base64url");
}

export function generateNonce(): string {
	return randomBytes(16).toString("base64url");
}

// --- Browser opening ---

export function openBrowser(url: string): void {
	// Launching is best-effort — the URL is also printed as a manual fallback —
	// so pass a no-op callback to swallow ENOENT/spawn errors instead of leaving
	// them unhandled.
	const ignore = (): void => {};
	if (process.platform === "darwin") {
		execFile("open", [url], ignore);
	} else if (process.platform === "win32") {
		execFile("cmd.exe", ["/c", "start", "", url], ignore);
	} else {
		execFile("xdg-open", [url], ignore);
	}
}

// --- Token exchange ---

export interface OAuthTokens {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
}

async function requestToken(
	params: Record<string, string>,
): Promise<OAuthTokens> {
	const res = await fetch(`${oauthBaseUrl()}/oauth/token`, {
		method: "POST",
		headers: { "Content-Type": "application/x-www-form-urlencoded" },
		body: new URLSearchParams(params).toString(),
	});

	if (!res.ok) {
		const body = await res.text();
		throw new Error(`OAuth token request failed (${res.status}): ${body}`);
	}

	const json = (await res.json()) as {
		access_token: string;
		refresh_token: string;
		expires_in: number;
	};

	return {
		accessToken: json.access_token,
		refreshToken: json.refresh_token,
		expiresIn: json.expires_in,
	};
}

export function exchangeCode(
	code: string,
	verifier: string,
): Promise<OAuthTokens> {
	return requestToken({
		grant_type: "authorization_code",
		client_id: CLI_CLIENT_ID,
		code,
		redirect_uri: CLI_REDIRECT_URI,
		code_verifier: verifier,
	});
}

export function refreshAccessToken(refreshToken: string): Promise<OAuthTokens> {
	return requestToken({
		grant_type: "refresh_token",
		client_id: CLI_CLIENT_ID,
		refresh_token: refreshToken,
	});
}

// --- Authorization URL ---

export function buildAuthUrl(
	challenge: string,
	state: string,
	nonce: string,
): string {
	const base = oauthBaseUrl();
	const params = new URLSearchParams({
		client_id: CLI_CLIENT_ID,
		response_type: "code",
		redirect_uri: CLI_REDIRECT_URI,
		code_challenge: challenge,
		code_challenge_method: "S256",
		scope: CLI_SCOPES.join(" "),
		state,
		nonce,
	});
	return `${base}/oauth/authorize?${params.toString()}`;
}

// --- Full OAuth login flow ---

export class OAuthError extends Error {
	constructor(
		public readonly code: string,
		message: string,
	) {
		super(message);
		this.name = "OAuthError";
	}
}

export interface OAuthLoginResult {
	profileName: string;
	identity: Identity;
	existed: boolean;
}

/**
 * Resolves which business account an OAuth user acts on behalf of.
 *
 * OAuth tokens are user-scoped (`user_xxx`), so list the business accounts the
 * user can manage (`GET /companies`) and pick one:
 *   - 0 business accounts → null (the user hasn't created one yet)
 *   - 1 business account  → use it
 *   - >1          → use --company-id if given, else prompt (TTY) / error (agent)
 */
async function resolveOAuthCompany(
	accessToken: string,
	opts: { agent: boolean; companyId?: string | undefined },
): Promise<Identity | null> {
	const whop = new Whop({ apiKey: accessToken, baseURL: apiBaseUrl() });

	// The spinner covers only the network fetch — it must stop before any prompt,
	// otherwise it renders on top of (and underneath) the business account picker.
	const s = opts.agent ? null : startSpinner("Loading your business accounts…");
	// Auto-paginate so a user with more than one page of business accounts can still be
	// resolved. When a specific --company-id is requested, stop as soon as it's
	// found; otherwise cap the scan so the picker can't grow unbounded.
	const MAX_COMPANIES = 1000;
	const companies: CompanyChoice[] = [];
	try {
		for await (const company of whop.companies.list({ first: 100 })) {
			companies.push({
				id: company.id,
				title: company.title,
				route: company.route,
			});
			if (opts.companyId && company.id === opts.companyId) break;
			if (companies.length >= MAX_COMPANIES) break;
		}
	} catch {
		s?.stop("Couldn't load your business accounts.");
		// An explicit --company-id can't be silently dropped: if the list failed to
		// load we can't verify it, so surface the error instead of logging in
		// without a business account. Without an explicit choice, stay non-fatal — commerce
		// commands will give a clear error rather than the login crashing.
		if (opts.companyId) {
			throw new OAuthError(
				"COMPANY_LOOKUP_FAILED",
				`Couldn't load your business accounts to verify "${opts.companyId}". Please try again.`,
			);
		}
		return null;
	}

	// An explicit --company-id is a hard request: verify it before the no-account
	// early return below, so an empty list errors (COMPANY_NOT_FOUND) rather than
	// silently logging the user in without the business account they asked for.
	if (opts.companyId) {
		const match = companies.find((company) => company.id === opts.companyId);
		if (!match) {
			s?.stop("");
			const available = companies.length
				? companies.map((company) => company.id).join(", ")
				: "none — this user has no business accounts yet";
			throw new OAuthError(
				"COMPANY_NOT_FOUND",
				`No business account "${opts.companyId}" for this user. Available: ${available}.`,
			);
		}
		s?.stop(`Using ${match.title}.`);
		return match;
	}

	if (companies.length === 0) {
		s?.stop("No business account found on your account.");
		return null;
	}

	if (companies.length === 1) {
		s?.stop(`Using ${companies[0].title}.`);
		return companies[0];
	}

	if (opts.agent) {
		s?.stop("");
		throw new OAuthError(
			"MULTIPLE_COMPANIES",
			`You can manage ${companies.length} business accounts — pass --company-id to choose one: ${companies
				.map((company) => `${company.id} (${company.title})`)
				.join(", ")}.`,
		);
	}

	// Stop the spinner before prompting so the picker is the only live UI.
	s?.stop(`Found ${companies.length} business accounts.`);
	return selectCompany(companies);
}

export async function performOAuthLogin(input: {
	profile?: string | undefined;
	agent?: boolean | undefined;
	companyId?: string | undefined;
}): Promise<OAuthLoginResult> {
	const { verifier, challenge } = generatePKCE();
	const state = generateState();
	const nonce = generateNonce();
	const authUrl = buildAuthUrl(challenge, state, nonce);

	const s = startSpinner("Opening browser…");
	openBrowser(authUrl);
	s.stop("Waiting for authorization in your browser…");
	printInfo(`If your browser didn't open, visit:\n${authUrl}`);

	let code: string;
	try {
		code = await startCallbackServer(state);
	} catch (err) {
		throw new OAuthError(
			"OAUTH_CALLBACK_FAILED",
			err instanceof Error ? err.message : "OAuth callback failed.",
		);
	}

	const s2 = startSpinner("Exchanging authorization code…");
	let tokens: OAuthTokens;
	try {
		tokens = await exchangeCode(code, verifier);
		s2.stop("Authorized.");
	} catch (err) {
		s2.stop("Token exchange failed.");
		throw new OAuthError(
			"TOKEN_EXCHANGE_FAILED",
			err instanceof Error ? err.message : "Token exchange failed.",
		);
	}

	const base = oauthBaseUrl();
	let identity: Identity = {};
	let companyIdentity: Identity | null = null;
	try {
		const res = await fetch(`${base}/oauth/userinfo`, {
			headers: { Authorization: `Bearer ${tokens.accessToken}` },
		});
		if (res.ok) {
			const info = (await res.json()) as {
				sub?: string;
				name?: string;
				email?: string;
			};
			identity = { id: info.sub, title: info.name, email: info.email };
		}
	} catch {
		// Non-fatal — profile name falls back to "default"
	}

	// Resolve the business account so scoped API calls work. OAuth tokens are
	// user-scoped, so we list the user's business accounts and pick one. The
	// spinner and picker are managed inside resolveOAuthCompany so they don't overlap.
	companyIdentity = await resolveOAuthCompany(tokens.accessToken, {
		agent: input.agent ?? false,
		companyId: input.companyId,
	});

	const name = input.profile ?? deriveProfileName(identity);
	const existed = listProfiles().some((p) => p.name === name);
	const tokenExpiresAt = new Date(
		Date.now() + tokens.expiresIn * 1000,
	).toISOString();

	const profile = upsertProfile({
		name,
		method: "oauth",
		secret: tokens.refreshToken,
		identity,
		companyIdentity: companyIdentity ?? undefined,
		accessToken: tokens.accessToken,
		tokenExpiresAt,
	});

	return {
		profileName: profile.name,
		identity,
		existed,
	};
}
